import React, { useState, useEffect } from 'react';
import { Result, Button, Card, List, Tag, Spin, Typography, Divider, Space, Confetti, Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { getProgress } from '../api/learn';
import { 
  TrophyOutlined, 
  StarFilled, 
  HeartFilled, 
  RocketOutlined,
  CheckCircleFilled,
  SmileOutlined 
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const Complete = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [incompleteSteps, setIncompleteSteps] = useState([]);

  const steps = [
    { key: 'step1_completed', name: '跟读准则', description: '朗读六项准则内容，准确率达到90%通过', icon: '🎤' },
    { key: 'step2_completed', name: '听播客', description: '收听准则讲解播客，加深理解', icon: '🎧' },
    { key: 'step3_completed', name: '完形填空', description: '填写准则关键词，巩固记忆', icon: '✏️' },
    { key: 'step4_completed', name: '评分反馈', description: '对六项准则进行评分', icon: '⭐' },
    { key: 'step5_completed', name: '找茬游戏', description: '找出场景中的违规行为', icon: '🔍' },
    { key: 'step6_completed', name: '自查承诺', description: '选择薄弱项，填写改进承诺', icon: '📝' },
  ];

  useEffect(() => {
    checkProgress();
  }, []);

  const checkProgress = async () => {
    try {
      const response = await getProgress();
      if (response.code === 200) {
        const progressData = response.data;
        setProgress(progressData);

        // 检查未完成的步骤
        const incomplete = steps.filter(step => !progressData[step.key]);
        setIncompleteSteps(incomplete);
      }
    } catch (error) {
      console.error('获取进度失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleContinue = () => {
    navigate('/learn');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果有未完成的步骤，显示未完成提示
  if (incompleteSteps.length > 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: 600, width: '100%', borderRadius: 12 }}>
          <Result
            status="warning"
            title={<span style={{ fontSize: 24 }}>学习尚未完成</span>}
            subTitle={`${user?.name || '您'}还有以下步骤未完成，请继续学习`}
          />
          
          <List
            header={<div style={{ fontWeight: 'bold', fontSize: 16 }}>未完成的步骤</div>}
            bordered
            dataSource={incompleteSteps}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Tag color="warning">{steps.findIndex(s => s.key === item.key) + 1}</Tag>}
                  title={item.name}
                  description={item.description}
                />
              </List.Item>
            )}
            style={{ marginBottom: 24 }}
          />

          <div style={{ textAlign: 'center' }}>
            <Button type="primary" size="large" onClick={handleContinue}>
              继续学习
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // 全部完成，显示成功页面
  const completedCount = steps.length;
  const userName = user?.name || '学员';

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 24px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Card 
        style={{ 
          maxWidth: 800, 
          width: '100%', 
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {/* 成功图标和标题 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ 
            fontSize: 80, 
            marginBottom: 16,
            animation: 'bounce 1s ease infinite'
          }}>
            🎉
          </div>
          <Title level={1} style={{ margin: 0, color: '#52c41a' }}>
            恭喜你完成学习任务！
          </Title>
          <Text type="secondary" style={{ fontSize: 18 }}>
            {userName}，你真棒！
          </Text>
        </div>

        <Divider />

        {/* 完成统计 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Space size={32}>
            <div>
              <div style={{ fontSize: 48, color: '#1890ff' }}>
                <TrophyOutlined />
              </div>
              <div style={{ marginTop: 8 }}>
                <Text strong style={{ fontSize: 24 }}>{completedCount}</Text>
                <Text type="secondary"> / {steps.length}</Text>
              </div>
              <Text type="secondary">完成步骤</Text>
            </div>
            <div>
              <div style={{ fontSize: 48, color: '#faad14' }}>
                <StarFilled />
              </div>
              <div style={{ marginTop: 8 }}>
                <Text strong style={{ fontSize: 24 }}>100</Text>
                <Text type="secondary"> 分</Text>
              </div>
              <Text type="secondary">学习积分</Text>
            </div>
            <div>
              <div style={{ fontSize: 48, color: '#eb2f96' }}>
                <HeartFilled />
              </div>
              <div style={{ marginTop: 8 }}>
                <Text strong style={{ fontSize: 24 }}>优秀</Text>
              </div>
              <Text type="secondary">学习评级</Text>
            </div>
          </Space>
        </div>

        <Divider />

        {/* 鼓励语 */}
        <Card 
          style={{ 
            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
            border: 'none',
            marginBottom: 24
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <RocketOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 16 }} />
            <Title level={3} style={{ color: '#2e7d32', margin: 0 }}>
              以行践言，以己之变，促企之兴
            </Title>
            <Paragraph style={{ fontSize: 16, color: '#388e3c', marginTop: 16, marginBottom: 0 }}>
              每一次学习都是成长的积累，每一次践行都是价值的体现。<br/>
              愿你以六项准则为指引，在工作中展现卓越风采！
            </Paragraph>
          </div>
        </Card>

        {/* 完成的步骤 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>已完成的学习步骤</Title>
          <Space wrap>
            {steps.map((step, index) => (
              <Tag 
                key={step.key}
                color="success"
                icon={<CheckCircleFilled />}
                style={{ 
                  padding: '8px 16px', 
                  fontSize: 14,
                  borderRadius: 20
                }}
              >
                {step.icon} {step.name}
              </Tag>
            ))}
          </Space>
        </div>

        <Divider />

        {/* 操作按钮 */}
        <div style={{ textAlign: 'center' }}>
          <Space size="middle">
            <Button 
              size="large" 
              onClick={handleLogout}
              style={{ minWidth: 120 }}
            >
              退出系统
            </Button>
            <Button 
              type="primary" 
              size="large" 
              onClick={() => navigate('/learn')}
              style={{ minWidth: 120 }}
            >
              再次学习
            </Button>
          </Space>
        </div>

        {/* 底部鼓励语 */}
        <div style={{ textAlign: 'center', marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Text type="secondary" style={{ fontSize: 14 }}>
            <SmileOutlined /> 继续保持，让优秀成为习惯！
          </Text>
        </div>
      </Card>

      {/* 添加简单的CSS动画 */}
      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
    </div>
  );
};

export default Complete;
